import React, { useEffect, useMemo, useState } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import Modal from '../../../ui-kit/Modal';
import Button from '../../../ui-kit/Button';
import { Plus, Trash2, Search } from 'lucide-react';
import { primaryAddress, addressLine } from '../logic/customerAddress';
import { MANUAL_SOURCES } from '../../orders/logic/orderSource';
import { usePartialCodConfig } from '../../orders/hooks/usePartialCodConfig';
import { useOrderPlacementGuard } from '../../orders/hooks/useOrderPlacementGuard';
import { repeatOrderWarning, lastOrderSummary } from '../../orders/logic/orderPlacementGuard';
import { discountSummary, discountDecision, DISCOUNT_APPROVAL_LIMIT } from '../../orders/logic/discountPolicy';
import { calculateShippingCost } from '../../orders/services/ShippingService';

const inputCls = 'w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm';
const COURIERS = ['Normal', 'Shiprocket', 'DTDC', 'India Post'];

// Profile Add Order — auto-fills customer, supports multi-item + an order discount,
// payment mode + courier preference. Submits to the SAME createOrder system.
const ProfileAddOrderModal = ({ isOpen, onClose, customer, onSubmit, loading, initialItems, shipTo }) => {
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    // Prices come from the product list and cannot be changed here — money off is
    // the Discount box only (a manager approves anything above the limit).
    const [items, setItems] = useState([]); // {sku, name, basePrice, qty}
    const [paymentMode, setPaymentMode] = useState('COD');
    const [courier, setCourier] = useState('Normal');
    const [source, setSource] = useState(MANUAL_SOURCES[0].value);
    const [codAdvance, setCodAdvance] = useState('');        // '' = full COD
    // ₹ off the whole order. A price box is for a different PRICE, never for a
    // reduction: DP07223 got a product at ₹-796 because there was nowhere else to
    // type "₹796 off".
    const [discount, setDiscount] = useState('');
    const partialCod = usePartialCodConfig();

    // The address the order will actually ship to. The profile resolves it (it can come
    // from the latest ORDER, not the customer record) and passes it in; resolving it here
    // again is the fallback for any caller that does not.
    const addr = shipTo || primaryAddress(customer);

    useEffect(() => {
        if (!isOpen) return;
        setItems(Array.isArray(initialItems) && initialItems.length ? initialItems : []);
        setSearch(''); setPaymentMode('COD'); setCourier('Normal'); setSource(MANUAL_SOURCES[0].value); setDiscount('');
        // Blank unless a non-zero default advance is configured.
        setCodAdvance(partialCod.defaultAdvance > 0 ? String(partialCod.defaultAdvance) : '');
        get(ref(getDatabase(), 'products')).then(s => {
            if (s.exists()) setProducts(Object.entries(s.val()).map(([sku, v]) => ({ sku, ...v })));
        });
    }, [isOpen]);

    const filtered = useMemo(() => !search ? [] : products.filter(p =>
        (p.name || '').toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase())).slice(0, 8), [products, search]);

    const addItem = (p) => {
        setItems(it => [...it, { sku: p.sku, name: p.name, basePrice: Number(p.priceAfterTax ?? p.priceMRP ?? p.price ?? 0), qty: 1 }]);
        setSearch('');
    };
    const upd = (i, k, v) => setItems(it => it.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
    const del = (i) => setItems(it => it.filter((_, idx) => idx !== i));

    const lineTotal = (x) => x.basePrice * Number(x.qty || 1);
    const subtotal = items.reduce((a, x) => a + lineTotal(x), 0);
    const discountNum = Math.round((Number(discount) || 0) * 100) / 100;
    // COD fee — the same charge the parser desk and the website add (Settings →
    // shipping: Kerala COD ₹44). This window used to send ₹0 for every charge, so its
    // COD orders were short by ₹44 (DP07294: courier asked for ₹500, not ₹544).
    const [codFeeRule, setCodFeeRule] = useState(0);
    const addrState = (shipTo || primaryAddress(customer))?.state || customer?.state || '';
    useEffect(() => {
        let live = true;
        if (!isOpen) return undefined;
        Promise.all([calculateShippingCost(addrState || 'Kerala', 'COD'), calculateShippingCost(addrState || 'Kerala', 'Prepaid')])
            .then(([cod, pre]) => { if (live) setCodFeeRule(Math.max(0, Number(cod) - Number(pre)) || 0); })
            .catch(() => { if (live) setCodFeeRule(44); });
        return () => { live = false; };
    }, [isOpen, addrState]);
    const codFee = paymentMode === 'COD' ? codFeeRule : 0;
    const total = Math.max(0, subtotal - Math.max(0, discountNum)) + codFee;

    const advanceNum = Math.max(0, Math.round(Number(codAdvance) || 0));
    // Always offered on a COD order — 0 (blank) is just a normal full COD.
    const showAdvance = paymentMode === 'COD';

    // Do Not Ship: a repeated-COD-return customer needs a manager to place the
    // order at all. Checked here, at the point of sale, not at dispatch.
    const { checkMobile, role } = useOrderPlacementGuard();
    const dSummary = discountSummary({ lines: items.map(x => ({ name: x.name, listPrice: x.basePrice, unitPrice: x.basePrice, qty: x.qty })), discount: discountNum });
    const dDecision = discountDecision(dSummary, role);
    const custMobile = customer?.primaryMobile || customer?.mobile || '';
    const dns = checkMobile(custMobile);

    // Ordering again within a fortnight is usually a duplicate.
    const orderList = useMemo(() => {
        const raw = customer?.orders;
        return Array.isArray(raw) ? raw : Object.values(raw || {});
    }, [customer]);
    const lastOrder = useMemo(
        () => [...orderList].sort((a, b) => new Date(b.orderDate || b.createdAt || 0) - new Date(a.orderDate || a.createdAt || 0))[0] || null,
        [orderList],
    );
    const repeat = repeatOrderWarning(customer?.lastOrderDate || lastOrder?.orderDate || lastOrder?.createdAt);

    const submit = () => {
        if (!items.length) { alert('Add at least one product.'); return; }
        if (dDecision.blocked) { alert(dDecision.blocked); return; }
        if (dDecision.needsApproval && !window.confirm(`${dDecision.warnings.join('\n')}\n\nCreate the order?`)) return;
        if (showAdvance && advanceNum >= total && total > 0) { alert('The advance cannot be the whole order — leave it blank for full COD.'); return; }
        // Do Not Ship — hard stop for anyone below manager, regardless of
        // payment mode: a serial returner costs us both courier legs even when
        // they prepay.
        if (dns.blocked && !dns.allowed) { alert(dns.reason); return; }
        if (dns.blocked && dns.allowed && !window.confirm(`${dns.reason}\n\nPlace this order anyway?`)) return;
        // Recent order — confirm rather than block.
        if (repeat.level !== 'none' && !window.confirm(
            `This customer ${repeat.label}${lastOrder ? `\n\nLast order: ${lastOrderSummary(lastOrder)}` : ''}\n\nCreate another order?`,
        )) return;
        onSubmit({
            customer: {
                name: customer?.name || '', mobile: customer?.primaryMobile || customer?.mobile || '',
                alternateMobile: customer?.secondaryMobile || '',
                address: addr.fullAddress || '',
                city: addr.city || customer?.city || '', state: addr.state || customer?.state || '',
                pincode: addr.pincode || customer?.pincode || '',
            },
            items: items.map(x => ({ sku: x.sku, qty: Number(x.qty || 1) })),
            payment: { mode: paymentMode, ...(codFee > 0 ? { codFee } : {}), ...(discountNum > 0 ? { discount: discountNum } : {}), ...(showAdvance && advanceNum > 0 ? { codAdvance: advanceNum } : {}) },
            shipping: { courier, courierPreference: courier },
            courierPreference: courier,
            shippingCost: 0,
            source,
        });
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add Order" className="max-w-2xl">
            <div className="space-y-3 max-h-[72vh] overflow-y-auto pr-1">
                {/* Auto-filled customer */}
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                    <div className="font-semibold text-slate-700">{customer?.name || '—'} · {customer?.primaryMobile || customer?.mobile}</div>
                    <div className="text-slate-500 text-xs">{addressLine(addr) || 'No address on file'}</div>
                    {!addr.fullAddress && (
                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5">
                            No street address — add one on the profile before shipping this order.
                        </div>
                    )}
                </div>

                {/* Do Not Ship — the loudest thing on this form. */}
                {dns.blocked && (
                    <div className={`rounded-lg border-2 px-3 py-2 text-sm ${dns.allowed ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-red-500 bg-red-50 text-red-800'}`}>
                        <div className="font-black uppercase tracking-wide">
                            {dns.allowed ? '⚠ Do Not Ship — manager override' : '⛔ Do Not Ship — manager approval required'}
                        </div>
                        <div className="text-xs mt-0.5 leading-snug">{dns.reason}</div>
                    </div>
                )}

                {/* Ordered recently — likely a duplicate. */}
                {repeat.level !== 'none' && (
                    <div className={`rounded-lg border px-3 py-2 text-xs ${repeat.level === 'high' ? 'border-orange-300 bg-orange-50 text-orange-900' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                        <span className="font-bold">
                            {repeat.level === 'high' ? '⚠ Possible duplicate — ' : 'Recent customer — '}
                            this customer {repeat.label}.
                        </span>
                        {lastOrder && <span className="block mt-0.5 text-[11px] opacity-90">Last order: {lastOrderSummary(lastOrder)}</span>}
                    </div>
                )}

                {/* Product search */}
                <div className="relative">
                    <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <input className={`${inputCls} pl-8`} placeholder="Search product to add…" value={search} onChange={e => setSearch(e.target.value)} />
                    {filtered.length > 0 && (
                        <div className="absolute z-10 left-0 right-0 bg-white border border-slate-200 rounded-lg mt-1 shadow max-h-48 overflow-y-auto">
                            {filtered.map(p => (
                                <button key={p.sku} onClick={() => addItem(p)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex justify-between items-center gap-2">
                                    <span className="flex flex-col">
                                        <span>{p.name}</span>
                                        <span className="text-[10px] font-mono text-slate-400">SKU: {p.sku}</span>
                                    </span>
                                    <span className="text-slate-400 shrink-0">₹{Number(p.priceAfterTax ?? p.priceMRP ?? 0)}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <p className="text-[11px] text-slate-400 -mt-1">Search and click to add multiple products. Prices come from the product list — for money off use the Discount box.</p>

                {/* Items */}
                {items.length > 0 && (
                    <div className="border border-slate-100 rounded-lg divide-y divide-slate-100">
                        {items.map((x, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 text-sm">
                                <span className="flex-1 min-w-0 flex flex-col">
                                    <span className="truncate">{x.name}</span>
                                    <span className="text-[10px] font-mono text-slate-400">SKU: {x.sku}</span>
                                </span>
                                <input type="number" min="1" className="w-14 px-1 py-1 border border-slate-200 rounded text-center" value={x.qty} onChange={e => upd(i, 'qty', e.target.value)} />
                                <span className="w-20 text-right text-slate-500">₹{x.basePrice}</span>
                                <span className="w-16 text-right font-medium">₹{lineTotal(x)}</span>
                                <button onClick={() => del(i)} className="text-red-500"><Trash2 size={14} /></button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-slate-500">Payment mode
                        <select className={inputCls} value={paymentMode} onChange={e => setPaymentMode(e.target.value)}><option>COD</option><option>Prepaid</option></select></label>
                    <label className="text-xs text-slate-500">Courier preference
                        <select className={inputCls} value={courier} onChange={e => setCourier(e.target.value)}>{COURIERS.map(c => <option key={c}>{c}</option>)}</select></label>
                    <label className="text-xs text-slate-500">Order source
                        <select className={inputCls} value={source} onChange={e => setSource(e.target.value)}>{MANUAL_SOURCES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
                    <label className="text-xs text-slate-500">Discount (₹ off the order)
                        <input type="number" min="0" className={inputCls} placeholder="0" value={discount} onChange={e => setDiscount(e.target.value)} />
                        <span className="text-[10px] text-slate-400">Above ₹{DISCOUNT_APPROVAL_LIMIT} needs a manager's approval</span></label>
                    {showAdvance && (
                        <label className="text-xs text-indigo-700 font-semibold">Advance to collect now (₹)
                            <input type="number" min="0" className={inputCls} placeholder="0 = full COD" value={codAdvance} onChange={e => setCodAdvance(e.target.value)} /></label>
                    )}
                </div>
                {showAdvance && advanceNum > 0 && (
                    <div className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 -mt-1">
                        Partial COD: collect ₹{advanceNum} now via a payment link, courier collects ₹{Math.max(0, total - advanceNum)} on delivery. Ships only after the advance is paid.
                    </div>
                )}

                {dDecision.blocked && (
                    <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 font-semibold">{dDecision.blocked}</div>
                )}
                {!dDecision.blocked && dDecision.warnings.length > 0 && (
                    <div className={`text-[11px] rounded px-2 py-1.5 border space-y-0.5 ${dSummary.payable === 0 || dSummary.zeroLines.length ? 'text-red-800 bg-red-50 border-red-300 font-semibold' : 'text-amber-900 bg-amber-50 border-amber-300'}`}>
                        {dDecision.warnings.map(w => <div key={w}>{w}</div>)}
                    </div>
                )}
                <div className="flex items-center justify-between pt-1">
                    <span className="font-bold text-slate-800">
                        {discountNum > 0 && !dDecision.blocked && <span className="font-normal text-slate-500 text-sm mr-2">₹{subtotal} − ₹{discountNum} =</span>}
                        {codFee > 0 && <span className="font-normal text-slate-500 text-sm mr-2">incl. ₹{codFee} COD fee ·</span>}
                        Total: ₹{total}
                    </span>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>Cancel</Button>
                        <Button onClick={submit} disabled={loading || !items.length}><Plus size={15} /> {loading ? 'Creating…' : 'Create Order'}</Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ProfileAddOrderModal;
